import styled from "@emotion/styled";
import { FC, createRef, useEffect, useRef, useState } from "react";

import {
  Button,
  DesignToken,
  Heading,
  Icon,
  Modal,
  ModalPortal,
  RadiusToken,
  SearchInput,
  ShadowToken,
  SpacingToken,
  Text,
  TransitionDurationToken,
  TransitionTimingToken,
} from "@livechat/design-system-react-components";

import { useDebounce } from "hooks/use-debounce";
import { WorkflowConditionType } from "routes/automations/workflows/interfaces";
import { textMultilineEllipsisStyles } from "styles/common";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";

import { ActionType } from "../actions/types";

import {
  ActionCategory,
  CATEGORY_ICON,
  CATEGORY_TITLE,
  IActionItem,
  ITriggerItem,
  TriggerCategory,
  actionCategories,
  actions,
  triggerCategories,
  triggers,
} from "./constants";

interface ICommonProps {
  isVisible: boolean;
  type: "action" | "trigger";
  mode: "add" | "edit";
  selectedAction?: ActionType;
  selectedTrigger?: WorkflowConditionType;
  onSelect: ((type: WorkflowConditionType) => void) | ((type: ActionType) => void);
  onClose: () => void;
}

interface ICommonActionProps extends ICommonProps {
  type: "action";
  onSelect: (type: ActionType) => void;
}

interface ICommonTriggerProps extends ICommonProps {
  type: "trigger";
  onSelect: (type: WorkflowConditionType) => void;
}

interface IAddActionProps extends ICommonActionProps {
  mode: "add";
}

interface IEditActionProps extends ICommonActionProps {
  mode: "edit";
  selectedAction?: ActionType;
}

interface IAddTriggerProps extends ICommonTriggerProps {
  mode: "add";
}

interface IEditTriggerProps extends ICommonTriggerProps {
  mode: "edit";
  selectedTrigger?: WorkflowConditionType;
}

export type IChooseActionOrTriggerModal = IEditActionProps | IAddActionProps | IEditTriggerProps | IAddTriggerProps;

interface IActionCategoryRef {
  assign?: React.RefObject<HTMLDivElement>;
  send?: React.RefObject<HTMLDivElement>;
  set?: React.RefObject<HTMLDivElement>;
  tag?: React.RefObject<HTMLDivElement>;
  spam?: React.RefObject<HTMLDivElement>;
  followers?: React.RefObject<HTMLDivElement>;
}

interface IConditionCategoryRef {
  ticket?: React.RefObject<HTMLDivElement>;
  assignment?: React.RefObject<HTMLDivElement>;
  requester?: React.RefObject<HTMLDivElement>;
  spam?: React.RefObject<HTMLDivElement>;
  time?: React.RefObject<HTMLDivElement>;
}

export type ICategoryRef = IActionCategoryRef & IConditionCategoryRef;

const isCategoryDisabled = (
  filteredEntities: IActionItem[] | ITriggerItem[],
  category: TriggerCategory | ActionCategory
): boolean => {
  return filteredEntities.filter(entity => entity.category === category).length === 0;
};

export const ChooseActionOrTriggerModal: FC<IChooseActionOrTriggerModal> = ({
  type,
  onClose,
  mode,
  isVisible,
  onSelect,
  selectedAction,
  selectedTrigger,
}) => {
  const searchRef = useRef<HTMLInputElement>(null);
  const activeOptionRef = useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = useState("");
  const [activeCategory, setActiveCategory] = useState<TriggerCategory | ActionCategory | null>(null);
  const debouncedSearchValue = useDebounce(searchValue, 200);

  const isActionModal = type === "action";

  const categoriesRef = useRef<ICategoryRef>(
    isActionModal
      ? {
          assign: undefined,
          send: undefined,
          set: undefined,
          tag: undefined,
          spam: undefined,
          followers: undefined,
        }
      : {
          ticket: undefined,
          assignment: undefined,
          requester: undefined,
          spam: undefined,
          time: undefined,
        }
  );

  const filteredEntities = isActionModal
    ? actions.filter(action => action.name.toLowerCase().includes(debouncedSearchValue.toLowerCase()))
    : triggers.filter(trigger => trigger.name.toLowerCase().includes(debouncedSearchValue.toLowerCase()));

  const visibleCategories = filteredEntities.map(entity => entity.category);

  filteredEntities.forEach(entity => {
    if (!categoriesRef.current[entity.category]) {
      categoriesRef.current[entity.category] = createRef();
    }
  });

  const handleShowAll = (): void => {
    setSearchValue("");
  };

  const handleCategoryClick = (category: TriggerCategory | ActionCategory): void => {
    if (categoriesRef.current[category]) {
      categoriesRef.current[category]?.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        const mostVisibleCategory = entries.reduce((acc, entry) => {
          if (entry.intersectionRatio > acc.intersectionRatio) {
            return entry;
          }
          return acc;
        });

        const category = mostVisibleCategory.target.children[0].id as TriggerCategory;

        if (mostVisibleCategory.isIntersecting && category !== activeCategory) {
          setActiveCategory(category);
        }
      },
      { threshold: [0.25, 0.5, 0.75] }
    );

    visibleCategories.forEach(category => {
      if (categoriesRef.current[category]?.current) {
        observer.observe(categoriesRef.current[category]!.current!);
      }
    });

    if (visibleCategories.length === 0) {
      setActiveCategory(null);
    }

    return (): void => {
      observer.disconnect();
    };
  }, [visibleCategories]);

  useEffect(() => {
    if (searchRef.current) {
      searchRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (activeOptionRef.current) {
      activeOptionRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [isVisible, activeOptionRef.current]);

  if (!isVisible) {
    return null;
  }

  return (
    <ModalPortal zIndex={StackingContextLevel.Modal}>
      <Modal
        heading={`
        ${mode === "add" ? "Add" : "Edit"} ${isActionModal ? "action" : "condition"}
      `}
        onClose={onClose}
      >
        <Container>
          <CategoriesColumn>
            <CategorySearchInput
              ref={searchRef}
              value={searchValue}
              onChange={setSearchValue}
              placeholder={isActionModal ? "Search for action" : "Search for condition"}
            />
            <CategoriesContainer>
              {(isActionModal ? actionCategories : triggerCategories).map(category => {
                const disabled = isCategoryDisabled(filteredEntities, category);

                return (
                  <CategoryItem
                    key={category}
                    active={category === activeCategory}
                    disabled={disabled}
                    onClick={() => handleCategoryClick(category)}
                    data-testid={category}
                  >
                    <Icon source={CATEGORY_ICON[category]} />
                    <Text>{CATEGORY_TITLE[category]}</Text>
                    <CategoryCounter disabled={disabled}>
                      {filteredEntities.filter(entity => entity.category === category).length}
                    </CategoryCounter>
                  </CategoryItem>
                );
              })}
            </CategoriesContainer>
          </CategoriesColumn>
          <OptionsColumn>
            {filteredEntities.length === 0 ? (
              <EmptyStateContainer>
                <Heading size="sm">No {isActionModal ? "actions" : "conditions"} found</Heading>
                <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>
                  Adjust your search criteria or show all {isActionModal ? "actions" : "conditions"}.
                </Text>
                <Button kind="link" onClick={handleShowAll}>
                  Show all {isActionModal ? "actions" : "conditions"}
                </Button>
              </EmptyStateContainer>
            ) : (
              <>
                {Object.keys(categoriesRef.current).map(category => {
                  const categoryFilteredEntities = filteredEntities.filter(entity => entity.category === category);

                  if (categoryFilteredEntities.length === 0) {
                    return null;
                  }

                  return (
                    <OptionCategoryContainer key={category} ref={categoriesRef.current[category as TriggerCategory]}>
                      <OptionCategoryHeader size="sm" id={category}>
                        {CATEGORY_TITLE[category as keyof typeof CATEGORY_TITLE]}{" "}
                        {isActionModal ? "actions" : "conditions"}
                      </OptionCategoryHeader>
                      {categoryFilteredEntities.map(entity => {
                        if (isActionModal) {
                          const { name, description, actionType } = entity as IActionItem;
                          return (
                            <Option
                              key={actionType}
                              ref={actionType === selectedAction ? activeOptionRef : undefined}
                              active={actionType === selectedAction}
                              onClick={() => onSelect(actionType)}
                            >
                              <Text bold>{name}</Text>
                              <OptionDescription size="sm">{description}</OptionDescription>
                            </Option>
                          );
                        } else {
                          const { name, description, triggerType } = entity as ITriggerItem;
                          return (
                            <Option
                              key={triggerType}
                              ref={triggerType === selectedTrigger ? activeOptionRef : undefined}
                              active={triggerType === selectedTrigger}
                              onClick={() => onSelect(triggerType)}
                            >
                              <Text bold>{name}</Text>
                              <OptionDescription size="sm">{description}</OptionDescription>
                            </Option>
                          );
                        }
                      })}
                    </OptionCategoryContainer>
                  );
                })}
              </>
            )}
          </OptionsColumn>
        </Container>
      </Modal>
    </ModalPortal>
  );
};

const Container = styled.div`
  display: flex;
`;

const CategoriesColumn = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: var(${SpacingToken.Spacing5});
  box-shadow: var(${ShadowToken.DividerRight});
`;

const CategorySearchInput = styled(SearchInput)`
  width: 240px;
  max-width: 100%;
`;

const CategoriesContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: var(${SpacingToken.Spacing4});
`;

const CategoryItem = styled.div<{ active: boolean; disabled: boolean }>`
  display: flex;
  align-items: center;
  gap: var(${SpacingToken.Spacing2});
  height: var(${SpacingToken.Spacing10});
  padding: 0 var(${SpacingToken.Spacing2});
  border-radius: var(${RadiusToken.Radius3});
  background-color: ${({ active }): string =>
    active ? withVar(DesignToken.PickerListOptionBackgroundActive) : "transparent"};
  cursor: pointer;

  transition: background-color var(${TransitionDurationToken.Moderate1}) var(${TransitionTimingToken.EaseOut});

  &:hover {
    background-color: ${({ active, disabled }): string =>
      disabled
        ? "transparent"
        : withVar(
            active ? DesignToken.PickerListOptionBackgroundActiveHover : DesignToken.PickerListOptionBackgroundHover
          )};
  }

  ${({ disabled }): string =>
    disabled
      ? `
        cursor: not-allowed;
        background-color: "transparent";
        color: var(${DesignToken.ContentBasicDisabled});
      `
      : ""};
`;

const CategoryCounter = styled(Text)<{ disabled: boolean }>`
  margin-left: auto;
  color: var(
    ${({ disabled }): string => (disabled ? DesignToken.ContentBasicDisabled : DesignToken.ContentBasicSecondary)}
  );
`;

const OptionsColumn = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 var(${SpacingToken.Spacing5});
  gap: var(${SpacingToken.Spacing2});
  height: 500px;
  max-height: 100%;
  overflow-y: auto;
`;

const OptionCategoryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(${SpacingToken.Spacing2});
`;

const Option = styled.div<{ active: boolean }>`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: var(${SpacingToken.Spacing4}) var(${SpacingToken.Spacing5});
  border-radius: var(${RadiusToken.Radius3});
  width: 460px;
  max-width: 100%;
  border: 1px solid
    var(${({ active }): string => (active ? DesignToken.ActionPrimaryDefault : DesignToken.BorderBasicPrimary)});
  transition: border-color var(${TransitionDurationToken.Fast2}) var(${TransitionTimingToken.EaseOut});

  &:hover {
    cursor: pointer;
    border-color: var(${DesignToken.BorderBasicHover});
  }
`;

const OptionCategoryHeader = styled(Text)`
  color: var(${DesignToken.ContentBasicSecondary});
`;

const OptionDescription = styled(Text)`
  color: var(${DesignToken.ContentBasicSecondary});
  ${textMultilineEllipsisStyles(2)}
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 460px;
  max-width: 100%;
  gap: var(${SpacingToken.Spacing2});
`;
